import AccordionButton from '@/components/accordion-button';
import InputControl from '@/components/form/input-control';
import TextInput from '@/components/form/text-input';
import Icon from '@/components/icon';
import Text from '@/components/text';
import ToggleTabs, { ToggleTabsInstance } from '@/components/toggle-tabs';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { DateFormat, getNiceDate } from '@/lib/helpers';
import { getTypedIdentProvider } from '@/lib/models/ident-provider';
import { IdentProviderModel } from '@/lib/models/ident-provider/ident-provider.model';
import { SamlIdentProviderModel } from '@/lib/models/ident-provider/saml-ident-provider.model';
import { getAuthCallbackUrl } from '@/lib/services/auth.service';
import { Box, Stack } from '@mui/material';
import { FC, RefObject } from 'react';

interface Props {
  formHandle: FormHandle<IdentProviderModel>;
  provider: SamlIdentProviderModel;
  readonly?: boolean;
  onChangeTab: (tabPath: string) => void;
  tabRef: RefObject<ToggleTabsInstance>;
}

const SamlIdentProviderForm: FC<Props> = ({
  formHandle,
  provider,
  readonly = false,
  onChangeTab,
  tabRef,
}) => {
  const { values, setFieldValue } = formHandle;
  const typedProvider = getTypedIdentProvider<SamlIdentProviderModel>(values);
  const { identityProviderConfig } = typedProvider;
  const { spEntityId, callbackUrl } = identityProviderConfig;

  const hasCallbackURI = !!callbackUrl;
  const callbackURI = hasCallbackURI
    ? typedProvider.identityProviderConfig.callbackUrl
    : getAuthCallbackUrl();

  const {
    parsedCertificate,
    certDates,
    identityProviderConfig: { samlMetadataUrl, samlMetadataXML },
  } = provider;
  const isNew = !provider.id;
  const hasCert = !!parsedCertificate;
  const hasUrl = !isNew && !!samlMetadataUrl;
  const hasXml = !isNew && !!samlMetadataXML;

  // the ident service PATCH call expects cert updates to be a string[], but the rawIdpCertificate field
  // below is defined as just a string, so ensure that value is provided in the expected format
  const handleChangeCert = (value: string) => {
    setFieldValue('identityProviderConfig.rawIdpCertificates', [value]);
  };

  const handleSetUrl = (value: string) => {
    setFieldValue('identityProviderConfig.samlMetadataUrl', value);
  };

  const handleSetXml = (value: string) => {
    setFieldValue('identityProviderConfig.samlMetadataXML', value);
  };

  const CertTitleEl = (
    <Stack gap={0.5} direction="row" alignItems="center">
      <Icon name="certificate" size="small" />
      <Text size="small" bold>
        Installed Certificate Details
      </Text>
    </Stack>
  );

  const panels = [
    {
      label: 'Metadata',
      path: 'SAML',
      component: (
        <Stack>
          <InputControl
            name="identityProviderConfig.idpEntityId"
            label="Entity Id"
            formHandle={formHandle}
            readonly={readonly}
          />

          <InputControl
            name="identityProviderConfig.idpSSOUrl"
            label="SSO URL"
            formHandle={formHandle}
            readonly={readonly}
          />

          <Box>
            {hasCert && (
              <Box mt={1}>
                <AccordionButton title={CertTitleEl}>
                  <Box mb={2}>
                    <Box mb={1}>
                      <Text color="grey" size="small" bold>
                        Certificate Fingerprint:
                      </Text>
                    </Box>
                    <Text>{parsedCertificate.x509Fingerprint || 'n/a'}</Text>
                  </Box>

                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Text color="grey" size="small">
                        Valid From:{' '}
                        {getNiceDate(certDates[0], DateFormat.FRIEND).formatted || 'n/a'}
                      </Text>
                    </Box>
                    <Text color="grey" size="small">
                      Valid To: {getNiceDate(certDates[1], DateFormat.FRIEND).formatted || 'n/a'}
                    </Text>
                  </Stack>
                </AccordionButton>
              </Box>
            )}

            <InputControl
              type="textarea"
              name="identityProviderConfig.rawIdpCertificate"
              formHandle={formHandle}
              rows={3}
              label="New Provider Certificate"
              onChange={handleChangeCert}
              readonly={readonly}
            />
          </Box>
        </Stack>
      ),
    },
    {
      label: 'Load from XML',
      path: 'SAML-XML',
      component: (
        <Stack gap={2}>
          <InputControl
            name="identityProviderConfig.newSamlMetadataXML"
            label="SAML XML"
            formHandle={formHandle}
            type="textarea"
            rows={6}
            readonly={readonly}
            onChange={handleSetXml}
          />
          {hasXml && (
            <TextInput
              name="previousSamlMetadataXML"
              label="Loaded XML"
              readonly
              type="textarea"
              rows={6}
              value={samlMetadataXML}
            />
          )}
        </Stack>
      ),
    },
    {
      label: 'Load from URL',
      path: 'SAML-URL',
      component: (
        <Stack gap={2}>
          <InputControl
            name="newSamlMetadataUrl"
            label="SAML URL"
            formHandle={formHandle}
            readonly={readonly}
            onChange={handleSetUrl}
          />
          {hasUrl && (
            <TextInput
              name="previousSamlMetadataUrl"
              label="Loaded URL"
              readonly
              copyClip
              value={samlMetadataUrl}
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {!isNew && (
        <Stack gap={2} mb={4}>
          <Stack gap={1}>
            <Text bold>Service Provider Entity ID</Text>
            <TextInput name="spEntityId" value={spEntityId} copyClip size="small" readonly />
          </Stack>

          <Stack gap={1}>
            <Text bold>Authorization Callback URL</Text>
            <TextInput name="authCallbackURI" value={callbackURI} copyClip size="small" readonly />
          </Stack>
        </Stack>
      )}

      <ToggleTabs panels={panels} onChange={onChangeTab} ref={tabRef} />
    </Box>
  );
};

export default SamlIdentProviderForm;
